import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  ALL_ARCHIVED_PURPOSE_ALT_TEXT,
  ALL_ARCHIVED_PURPOSE_STRING,
  ALL_ARCHIVED_PURPOSE_TEXT,
  NOT_ARCHIVED_PURPOSE_STRING,
  NOT_ARCHIVED_PURPOSE_TEXT,
  OTHER_PURPOSE,
} from 'archive/ArchiveConstants';
import {FilterKey, MultiValueFilter} from 'search/SearchConstants';
import {GRAY} from 'ui-library/Colors';
import {OptionData} from 'ui-library/menus/MenuConstants';
import pick from 'util/pick';

import DropdownFilterForm, {prefixedToggleText} from './base/DropdownFilterForm';
import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';

interface ArchivePurposesFilter extends MultiValueFilter {
  key: FilterKey.ARCHIVE_PURPOSES;
}
interface ArchivePurposesFilterFormProps extends BaseFilterFormProps {
  filter: ArchivePurposesFilter;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: ArchivePurposesFilter) => void;
  onRemove?: () => void;
  archivePurposes: Array<string> | null;
  hideNotArchivedOption?: boolean;
  hideAllArchivedOption?: boolean;
}
export interface ArchivePurposesFilterFormExtraProps {
  archivePurposes: Array<string>;
}

function ArchivePurposesFilterForm(
  props: ArchivePurposesFilterFormProps & ArchivePurposesFilterFormExtraProps
): JSX.Element {
  const notArchivedOption = {
    name: NOT_ARCHIVED_PURPOSE_TEXT,
    value: NOT_ARCHIVED_PURPOSE_STRING,
    className: css(styles.allArchivedOption),
  };
  const allArchivedOption = {
    name: ALL_ARCHIVED_PURPOSE_ALT_TEXT,
    value: ALL_ARCHIVED_PURPOSE_STRING,
    className: css(styles.allArchivedOption),
  };
  const defaultArchivedOption = {
    name: ALL_ARCHIVED_PURPOSE_TEXT,
    value: OTHER_PURPOSE,
  };

  const archivedOptions = props.archivePurposes.length
    ? props.archivePurposes.map((purpose) => ({
        name: purpose,
        value: purpose,
      }))
    : [defaultArchivedOption];
  const optionDatas = [
    ...(props.hideNotArchivedOption ? [] : [notArchivedOption]),
    ...(props.hideAllArchivedOption || !props.archivePurposes.length ? [] : [allArchivedOption]),
    ...archivedOptions,
  ];
  const skeletonProps = pick(props, ...BASE_FILTER_FORM_PROP_NAMES);
  return (
    <DropdownFilterForm
      {...skeletonProps}
      dropdownToggleText={prefixedToggleText(
        props.hideNotArchivedOption === true ? 'Archive Reasons' : 'Archived',
        optionDatas,
        props.filter
      )}
      filterStyle={props.filterStyle}
      isMulti={true}
      onChangeValue={(value: Array<string>, selectedValue?: string) => {
        const allOptionValues = archivedOptions.map((o) => o.value);
        const cleanedValue = handleAllArchivedOption(
          value,
          notArchivedOption,
          allArchivedOption,
          allOptionValues,
          selectedValue
        );
        props.onChangeFilter({...props.filter, value: cleanedValue});
      }}
      onRemove={props.onRemove}
      optionDatas={optionDatas}
      value={props.filter.value}
    />
  );
}

function handleAllArchivedOption(
  value: Array<string>,
  notArchivedOption: OptionData,
  allArchivedOption: OptionData,
  allOptionValues: Array<string>,
  selectedValue?: string
): Array<string> {
  const a = selectedValue === allArchivedOption.value;
  const b = value.includes(allArchivedOption.value);

  if (a && b) {
    // add all archive options
    return [...new Set([...value, ...allOptionValues])];
  } else if (a) {
    // remove all archive options
    return value.filter((v) => !allOptionValues.includes(v));
  } else if (b) {
    if (selectedValue === notArchivedOption.value) {
      return value;
    } else {
      // remove allArchivedOption
      return value.filter((v) => v !== allArchivedOption.value);
    }
  }

  // possibly add allArchivedOption
  if (
    (value.includes(notArchivedOption.value) && value.length === allOptionValues.length + 1) ||
    (!value.includes(notArchivedOption.value) && value.length === allOptionValues.length)
  ) {
    return [allArchivedOption.value, ...value];
  }

  return value;
}

export default ArchivePurposesFilterForm;

const styles = StyleSheet.create({
  allArchivedOption: {
    borderBottom: `1px solid ${GRAY}`,
  },
});
